import forge from 'apis/forge';
import { Loader } from 'components/Widget/Loader';
import { Scene } from 'dragonmouse-shared/interfaces';
import { useCallback, useEffect, useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { selectUser } from 'redux/db/reducers';
import { RootReducer } from 'redux/reducers';
import { validEmail } from 'utils';
import Modal from '../..';
import * as S from './styles';

interface SceneUserManagerModalProps {
	scene: Scene;
	onBack: () => void;
	onClose: () => void;
}

const SceneUserManagerModal = ({ scene, onBack, onClose }: SceneUserManagerModalProps) => {
	const [sceneUserEmails, setSceneUserEmails] = useState<string[]>([]);
	const [displayUsers, setDisplayUsers] = useState<string[]>([]);
	const [emailList, setEmailList] = useState('');
	const [isAddingUsers, setIsAddingUsers] = useState(false);

	// presence of loadingText indicates that the modal is loading
	const [loadingText, setLoadingText] = useState<string | null>(null);
	const [removingUser, setRemovingUser] = useState<string | null>(null);

	const { authUser, user } = useSelector((r: RootReducer) => {
		return {
			authUser: r.mode.authUser,
			user: selectUser(r.db),
		};
	}, shallowEqual);

	//------------------------------------------------------------------------------------------------
	// HOOKS
	//------------------------------------------------------------------------------------------------
	useEffect(() => {
		const fetchSceneUsers = async () => {
			setLoadingText('Loading users...');
			try {
				const response = await forge.getSceneUsers({ sceneId: scene.id });
				setSceneUserEmails(response.emails);
			} catch (error) {
				toast.error('Failed to fetch scene users');
			} finally {
				setLoadingText(null);
			}
		};

		fetchSceneUsers();
	}, [scene.id]);

	useEffect(() => {
		const sortedUsers = sceneUserEmails
			.filter((email) => email !== authUser?.email)
			.sort((a, b) => a.localeCompare(b));
		setDisplayUsers(sortedUsers);
	}, [sceneUserEmails]);

	//------------------------------------------------------------------------------------------------
	// HANDLERS
	//------------------------------------------------------------------------------------------------
	const onRemoveUser = async (email: string) => {
		setRemovingUser(email);
		try {
			await forge.revokeSceneAccess.withToast()({ sceneId: scene.id, email });
			setSceneUserEmails((prev) => prev.filter((e) => e !== email));
			toast.success('User access revoked successfully');
		} catch (error) {
			toast.error('Failed to revoke user access');
		} finally {
			setRemovingUser(null);
		}
	};

	const onAddUser = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!emailList) return;
		const emails: string[] = emailList.split(',').map((email) => email.trim());

		// filter out any emails that aleady have access to this scene
		const emailsWithAccess = sceneUserEmails.filter((email) => emails.includes(email));
		if (emailsWithAccess.length === 1) {
			toast.error('The following email address already has access: ' + emailsWithAccess[0]);
			return;
		}
		if (emailsWithAccess.length > 1) {
			toast.error(
				'The following email addresses already have access: ' + emailsWithAccess.join(', ')
			);
			return;
		}

		// filter out any emails that are invalid
		const invalidEmails = emails.filter((email) => !validEmail(email));
		if (invalidEmails.length === 1) {
			toast.error('The following email address is invalid: ' + invalidEmails[0]);
			return;
		}
		if (invalidEmails.length > 1) {
			toast.error('The following email addresses are invalid: ' + invalidEmails.join(', '));
			return;
		}
		if (emails.length === 0) {
			toast.error('No valid email addresses provided');
			return;
		}

		setIsAddingUsers(true);
		try {
			await forge.grantSceneAccess.withToast()({ sceneId: scene.id, emails });
			// we aggressively update the email list the modal uses.
			// we expect electric to soon send an update that matches this
			setSceneUserEmails((prev) => [...prev, ...emails]);
			setEmailList('');
			toast.success('User access granted successfully');
			forge.updateUser({
				update: {
					id: user?.id,
					metadata: {
						lastSharedDate: new Date().toISOString(),
					},
				},
			});
		} catch (error) {
			// we don't show an error, in practice the server throws an error and that is shown
			// due to the way the forge API is set up
		} finally {
			setIsAddingUsers(false);
		}
	};

	const close = useCallback(() => {
		onClose();
	}, [onClose]);

	//------------------------------------------------------------------------------------------------
	// RENDERING
	//------------------------------------------------------------------------------------------------
	return (
		<Modal
			title={'Manage Project Editors'}
			cancelLabel="Close"
			submitLabel="Save"
			onBack={onBack}
			onClose={close}
		>
			{loadingText ? (
				<S.LoadingContainer>
					<Loader width={32} height={32} />
					<S.LoadingText>{loadingText}</S.LoadingText>
				</S.LoadingContainer>
			) : (
				<>
					<S.AddUserSection as="form" onSubmit={onAddUser}>
						<S.EmailInput
							type="text"
							placeholder="Emails, comma separated"
							value={emailList}
							onChange={(e) => setEmailList(e.target.value)}
							disabled={isAddingUsers}
						/>
						<S.AddButton type="submit" disabled={isAddingUsers || !emailList}>
							{isAddingUsers ? <Loader width={16} height={16} /> : 'Add'}
						</S.AddButton>
					</S.AddUserSection>
					<S.Divider />
					<S.SectionTitle>People with access</S.SectionTitle>
					<S.UserList>
						{authUser?.email && <S.UserItem key={authUser.email}>{authUser.email}</S.UserItem>}
						{displayUsers.map((email) => (
							<S.UserItem key={email}>
								{email}
								<S.RemoveButton
									onClick={() => onRemoveUser(email)}
									disabled={removingUser === email}
								>
									{removingUser === email ? <Loader width={16} height={16} /> : 'remove'}
								</S.RemoveButton>
							</S.UserItem>
						))}
					</S.UserList>
				</>
			)}
		</Modal>
	);
};

export default SceneUserManagerModal;
